import React from 'react';
import { Avatar, CircularProgress } from '@mui/material';
import { uploadsUrl } from '../utils/api';
import { useUploadBlobUrl } from '../hooks/useUploadBlobUrl';

/** Stored filename or relative path — use authenticated /api/uploads */
export function memberProfilePhotoFilename(profilePicture) {
  if (!profilePicture) return null;
  const s = String(profilePicture).trim();
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      const m = (u.pathname || '').match(/(?:\/api)?\/uploads\/([^/]+)$/i);
      if (m && m[1] && !/[^a-zA-Z0-9._-]/.test(m[1])) return m[1];
    } catch {
      return null;
    }
    return null;
  }
  return s
    .replace(/^\/+/, '')
    .replace(/^(api\/)?uploads\//i, '');
}

function memberInitials(member) {
  const a = (member?.first_name?.[0] || '').trim();
  const b = (member?.last_name?.[0] || '').trim();
  const two = `${a}${b}`.toUpperCase();
  if (two) return two;
  return (a || member?.first_name?.[0] || 'M').toUpperCase();
}

/**
 * Member profile photo for admin UI and member portal (list, detail, ID card).
 * Protected files load via useUploadBlobUrl (data: URL) or static /uploads URL on Avatar.src.
 */
export default function MemberProfileAvatar({ member, size, sx: extra = {} }) {
  const filename = memberProfilePhotoFilename(member?.profile_picture);
  const { url: imageUrl, loading } = useUploadBlobUrl(filename);
  const initials = memberInitials(member);

  /** Applied last so callers cannot accidentally widen the avatar (flex stretch → oval). */
  const squareLock = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    maxWidth: size,
    maxHeight: size,
    flexShrink: 0,
    alignSelf: 'center',
    aspectRatio: '1 / 1',
    borderRadius: '50%',
  };

  const baseSx = {
    bgcolor: 'primary.main',
    fontSize: Math.max(14, size * 0.28),
    border: '3px solid',
    borderColor: 'divider',
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box',
    '& img': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      display: 'block',
    },
    ...extra,
    ...squareLock,
  };

  // Protected upload: filename stored — data URL on Avatar.src so the image fills the circle
  if (filename) {
    return (
      <Avatar variant="circular" src={imageUrl || undefined} sx={baseSx} imgProps={{ alt: '' }}>
        {loading ? (
          <CircularProgress size={Math.max(20, size * 0.38)} thickness={4} color="inherit" />
        ) : (
          initials
        )}
      </Avatar>
    );
  }

  const raw = member?.profile_picture;
  const direct =
    raw && String(raw).startsWith('http') ? String(raw) : raw ? uploadsUrl(raw) : null;

  return (
    <Avatar variant="circular" src={direct || undefined} sx={baseSx} imgProps={{ alt: '' }}>
      {initials}
    </Avatar>
  );
}
